// ────────────────────────────────────────────────────────────────────
// ⚠️ TIER-2 CLEANUP (VIN-2026-27, 2026-05-26) — TRIM AT PRODUCTION MIGRATION
//
// This file unions the legacy `eventInvitations` flat collection with
// the unified collectionGroup path. The legacy reads (search for
// `_src: 'legacy'`) are dead weight after the legacy events/ data
// wipe — but they're still load-bearing until that wipe happens
// (pre-cutover invites otherwise become invisible).
//
// PRODUCTION-MIGRATION CHECKLIST:
//   1. Wipe legacy `events/` collection AND `eventInvitations/` collection.
//   2. Remove every `_src: 'legacy'` branch + legacySnap union in this file.
//   3. Drop the legacy `eventInvitations/*` rules block in firestore.rules.
// ────────────────────────────────────────────────────────────────────

import {
  collection, collectionGroup, query, where, getDocs, doc, getDoc,
  updateDoc, serverTimestamp,
} from 'firebase/firestore'

const APP_URL = 'https://www.vinarai.com'

export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase()
}

export function buildEventInviteLink(eventId, inviteId = '', email = '', orgId = '') {
  const url = new URL(`${APP_URL}/event/${eventId}/join`)
  if (inviteId) url.searchParams.set('invite', inviteId)
  // VIN-2026-84: carry orgId so JoinEvent can read the event DIRECTLY
  // (organizations/{orgId}/events/{eventId}). A guest can't run getEventById's
  // collection-group query — Firestore rejects collection-group queries when
  // the rules can't prove every match is readable — so without orgId the link
  // dead-ends at "Event Not Found". A direct doc read is evaluated against that
  // one doc and passes the invite_only read rule.
  if (orgId) url.searchParams.set('org', orgId)
  const normalized = normalizeEmail(email)
  if (normalized) url.searchParams.set('email', normalized)
  return url.toString()
}

export async function claimEventInvitesForUser(db, user) {
  if (!user?.uid) return []
  const emailNorm = normalizeEmail(user.email)
  if (!emailNorm) return []

  // Scan BOTH paths — legacy flat eventInvitations AND the unified
  // collection-group invitations under organizations/{orgId}/events.
  // Without the unified scan, an email-only invite created via the
  // Phase 3 dual-write stays invisible after sign-in (the read rule
  // requires toUserId == auth.uid; an unclaimed invite has toUserId
  // == ''). Claiming stamps the recipient's uid on the doc so
  // subsequent rule checks pass.
  const [legacySnap, unifiedSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'eventInvitations'),
      where('toEmailNorm', '==', emailNorm),
    )).catch(() => ({ docs: [] })),
    getDocs(query(
      collectionGroup(db, 'invitations'),
      where('toEmailNorm', '==', emailNorm),
    )).catch(() => ({ docs: [] })),
  ])

  const resolvedName = user.displayName || user.email?.split('@')[0] || 'Guest'
  const claimed = []

  // Legacy claim — same logic as before, just split out.
  for (const d of legacySnap.docs) {
    const data = d.data()
    if (data.toUserId && data.toUserId !== user.uid) continue
    if (data.toUserId === user.uid) {
      claimed.push({ id: d.id, ...data, _src: 'legacy' })
      continue
    }
    await updateDoc(doc(db, 'eventInvitations', d.id), {
      toUserId:    user.uid,
      toUserName:  data.toUserName || resolvedName,
      toEmail:     data.toEmail   || user.email || '',
      toEmailNorm: emailNorm,
      claimedAt:   serverTimestamp(),
    }).catch(() => {})
    claimed.push({
      id: d.id,
      ...data,
      toUserId:    user.uid,
      toUserName:  user.displayName || data.toUserName || 'Guest',
      toEmail:     data.toEmail || user.email || '',
      toEmailNorm: emailNorm,
      _src:        'legacy',
    })
  }

  // Unified claim — same idea, against the new path. We can update by
  // d.ref directly since we already have the parent path from the
  // collection-group result.
  for (const d of unifiedSnap.docs) {
    const data = d.data()
    if (data.toUserId && data.toUserId !== user.uid) continue
    if (data.toUserId === user.uid) {
      claimed.push({ id: d.id, ...data, _src: 'unified', _path: d.ref.path })
      continue
    }
    await updateDoc(d.ref, {
      toUserId:    user.uid,
      toUserName:  data.toUserName || resolvedName,
      toEmail:     data.toEmail   || user.email || '',
      toEmailNorm: emailNorm,
      claimedAt:   serverTimestamp(),
    }).catch(() => {})
    claimed.push({
      id: d.id,
      ...data,
      toUserId:    user.uid,
      toUserName:  user.displayName || data.toUserName || 'Guest',
      toEmail:     data.toEmail || user.email || '',
      toEmailNorm: emailNorm,
      _src:        'unified',
      _path:       d.ref.path,
    })
  }

  return claimed
}

export async function fetchEventInvitesForUser(db, user, options = {}) {
  const { includeEnded = false } = options
  if (!user?.uid) return []
  await claimEventInvitesForUser(db, user).catch(() => {})

  const emailNorm = normalizeEmail(user.email)

  // Phase 3 dual-read: union the legacy flat eventInvitations
  // collection AND the new collection-group `invitations` under
  // organizations/{orgId}/events/{eventId}/invitations. New-path reads
  // get the source of truth post-Phase-3 cutover; legacy reads cover
  // any unmigrated test data until the wipe.
  const byUidLegacyPromise = getDocs(query(
    collection(db, 'eventInvitations'),
    where('toUserId', '==', user.uid),
  )).catch(() => ({ docs: [] }))

  const byEmailLegacyPromise = emailNorm
    ? getDocs(query(
        collection(db, 'eventInvitations'),
        where('toEmailNorm', '==', emailNorm),
      )).catch(() => ({ docs: [] }))
    : Promise.resolve({ docs: [] })

  const byUidUnifiedPromise = getDocs(query(
    collectionGroup(db, 'invitations'),
    where('toUserId', '==', user.uid),
  )).catch(() => ({ docs: [] }))

  const byEmailUnifiedPromise = emailNorm
    ? getDocs(query(
        collectionGroup(db, 'invitations'),
        where('toEmailNorm', '==', emailNorm),
      )).catch(() => ({ docs: [] }))
    : Promise.resolve({ docs: [] })

  const [byUidLegacy, byEmailLegacy, byUidUnified, byEmailUnified] = await Promise.all([
    byUidLegacyPromise, byEmailLegacyPromise, byUidUnifiedPromise, byEmailUnifiedPromise,
  ])

  // Logical-key dedup: legacy and unified copies of the same logical
  // invitation have DIFFERENT doc ids (random per addDoc on each path),
  // so a doc-id-keyed Map double-counts them. Key on
  // (eventId, toUserId|toEmailNorm) so the same recipient on the same
  // event collapses regardless of source. Prefer the unified row when
  // both exist — it's the path with first-class rules + future-proof
  // queries.
  const merged = new Map()

  const annotateUnified = (d) => {
    const path = d.ref.path.split('/')
    // path: organizations/{orgId}/events/{eventId}/invitations/{inviteId}
    return {
      id:       d.id,
      ...d.data(),
      _src:     'unified',
      _orgId:   path[1],
      _eventId: path[3],
      _path:    d.ref.path,
    }
  }
  const annotateLegacy = (d) => ({ id: d.id, ...d.data(), _src: 'legacy' })

  const logicalKey = (data) => {
    const eid = data.eventId || ''
    const recipient = data.toUserId || data.toEmailNorm || data.toEmail || ''
    return `${eid}:${recipient}`
  }
  // Insert legacy first, then unified — unified overwrites because we
  // prefer it as the source of truth post-cutover.
  for (const d of byUidLegacy.docs)   merged.set(logicalKey(d.data()), annotateLegacy(d))
  for (const d of byEmailLegacy.docs) merged.set(logicalKey(d.data()), annotateLegacy(d))
  for (const d of byUidUnified.docs)  merged.set(logicalKey(d.data()), annotateUnified(d))
  for (const d of byEmailUnified.docs) merged.set(logicalKey(d.data()), annotateUnified(d))

  const filtered = [...merged.values()].filter(data => {
    if (data.deletedByInvitee)     return false
    if (data.rsvp === 'cancelled') return false
    // Ignore invites explicitly assigned to another user.
    if (data.toUserId && data.toUserId !== user.uid) return false
    return true
  })

  // Fetch event statuses to filter out completed/cancelled events.
  // Unified rows carry _orgId so we can address the new path directly;
  // legacy rows fall back to the top-level events/ collection.
  const eventStatuses = {}
  await Promise.all(filtered.map(async inv => {
    if (!inv.eventId) return
    try {
      let snap = null
      if (inv._src === 'unified' && inv._orgId) {
        snap = await getDoc(doc(db, 'organizations', inv._orgId, 'events', inv.eventId))
      } else {
        snap = await getDoc(doc(db, 'events', inv.eventId))
      }
      if (snap?.exists()) eventStatuses[inv.eventId] = snap.data().status
    } catch {}
  }))

  return filtered
    .filter(inv => {
      if (includeEnded) return true
      const status = eventStatuses[inv.eventId]
      // Unified statuses use 'completed' / 'cancelled'; legacy uses
      // 'complete' / 'cancelled'. Filter both.
      return status !== 'complete' && status !== 'completed' && status !== 'cancelled'
    })
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
}
